import {
  fetchEventSource,
  type EventSourceMessage,
} from "@microsoft/fetch-event-source";
import type { AgentEvent } from "./types";

export interface ObserveCallbacks {
  /**
   * eventSeq is the server-side event-log sequence number (SSE `id:` line).
   * The caller tracks it as its replay cursor: a re-attach with that cursor
   * replays only the events it hasn't seen yet.
   */
  onEvent: (event: AgentEvent, eventSeq?: number) => void;
  /** The stream ended (server closed it after the log was exhausted). */
  onClose: () => void;
  /** Final HTTP-level or unexpected failure. Transport blips auto-retry. */
  onError: (err: unknown) => void;
  /**
   * The server has nothing to observe for this session (204): no run in
   * flight, none paused on clarify, or the log expired past the retention
   * window. The caller falls back to the persisted messages.
   */
  onNoRun: () => void;
}

export interface StreamHandle {
  abort: () => void;
}

/** Sentinel thrown from onopen on a 204 so onerror can route it to onNoRun. */
const NO_RUN = Symbol("no_run");

function parseSeq(msg: EventSourceMessage): number | undefined {
  if (!msg.id) return undefined;
  const n = Number(msg.id);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Observe a session's agent run via `GET /api/chat/sessions/{id}/stream` —
 * the ONLY streaming channel in the app. Starting a run is a separate plain
 * POST command; this request is a pure read, so it is always safe to open,
 * re-open, and auto-retry.
 *
 * The server replays its event log after `lastEventId` (0 = from the
 * beginning), then follows live. fetchEventSource tracks the SSE `id:` lines
 * and re-sends the updated `last-event-id` header on transport retries,
 * making the replay exact. HTTP-level failures (4xx/5xx) and a 204 "nothing
 * to observe" are final.
 */
export function observeSessionStream(
  sessionId: number,
  lastEventId: number,
  cbs: ObserveCallbacks,
): StreamHandle {
  const ctrl = new AbortController();

  const headers: Record<string, string> = { accept: "text/event-stream" };
  if (lastEventId > 0) {
    // Lowercase to match the key fetchEventSource itself maintains on retry.
    headers["last-event-id"] = String(lastEventId);
  }

  fetchEventSource(`/api/chat/sessions/${sessionId}/stream`, {
    method: "GET",
    headers,
    credentials: "same-origin",
    signal: ctrl.signal,
    // Keep observing across tab visibility changes; the run is server-owned
    // and events must keep flowing into the store while the tab is hidden.
    openWhenHidden: true,

    async onopen(res) {
      if (res.status === 204) throw NO_RUN;
      const ct = res.headers.get("content-type") ?? "";
      if (res.ok && ct.includes("text/event-stream")) return;

      // Non-SSE response: drain body and surface it via onerror.
      let body: unknown = null;
      try {
        const text = await res.text();
        if (text) body = JSON.parse(text);
      } catch {
        // ignore JSON parse errors
      }
      throw { status: res.status, body };
    },

    onmessage(msg: EventSourceMessage) {
      if (!msg.data) return;
      let parsed: AgentEvent;
      try {
        parsed = JSON.parse(msg.data) as AgentEvent;
      } catch (err) {
        console.warn("invalid SSE payload", msg.data, err);
        return;
      }
      cbs.onEvent(parsed, parseSeq(msg));
    },

    onclose() {
      cbs.onClose();
    },

    onerror(err) {
      if (err === NO_RUN) {
        cbs.onNoRun();
        throw err; // stop; the promise catch below swallows the sentinel
      }
      if (err && typeof err === "object" && "status" in err) {
        // HTTP-level failure — final, don't hammer the server.
        cbs.onError(err);
        throw err;
      }
      // Plain transport error: return undefined so fetchEventSource retries
      // with the tracked last-event-id cursor.
      return undefined;
    },
  }).catch((err) => {
    if (ctrl.signal.aborted || err === NO_RUN) return;
    cbs.onError(err);
  });

  return { abort: () => ctrl.abort() };
}
